import { useQuery } from "@tanstack/react-query";
import { api, fmtNum, CHART_COLORS } from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell,
} from "recharts";

function Panel({ title, children, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-5" data-testid={testId}>
      <h3 className="mb-4 font-heading text-lg font-medium">{title}</h3>
      {children}
    </div>
  );
}

const axisStyle = { fontSize: 11, fill: "#575E5A" };
const tip = { borderRadius: 8, border: "1px solid #e5e2dc", fontSize: 12 };

export default function Growth() {
  const { data } = useQuery({ queryKey: ["growth"], queryFn: () => api.get("/stats/growth").then((r) => r.data) });

  return (
    <div className="space-y-6" data-testid="growth-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Growth Analytics</h1>
        <p className="mt-1 text-sm text-muted-foreground">Client acquisition trends across time, geography and referrals.</p>
      </div>

      <Panel title="New Clients by Month" testId="chart-by-month">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data?.by_month || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
            <XAxis dataKey="month" tick={axisStyle} tickLine={false} axisLine={false} />
            <YAxis tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tip} />
            <Line type="monotone" dataKey="clients" stroke="#143a4d" strokeWidth={2.5} dot={{ r: 3, fill: "#143a4d" }} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Top Countries" testId="chart-by-country">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data?.by_country || []} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" horizontal={false} />
              <XAxis type="number" tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} width={90} />
              <Tooltip contentStyle={tip} cursor={{ fill: "#00000008" }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {(data?.by_country || []).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Top Referrals by Clients" testId="chart-by-referral">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data?.by_referral || []} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#00000010" horizontal={false} />
              <XAxis type="number" tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} width={90} />
              <Tooltip contentStyle={tip} cursor={{ fill: "#00000008" }} />
              <Bar dataKey="value" fill="#2b9bb7" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Clients by KYC Status" testId="chart-by-kyc-bar">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data?.by_kyc || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
            <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
            <YAxis tick={axisStyle} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tip} cursor={{ fill: "#00000008" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {(data?.by_kyc || []).map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
